import { useState } from 'react';
import axios from 'axios';
import {
  branchTermSingular,
  branchTermSingularCapitalised,
  branchTermPlural,
  branchTermPluralCapitalised,
} from 'utils/terminology';
import SelectAStoreMap from './select_a_store_map';
import SelectAStoreResult from './select_a_store_result';

const SelectAStore = ({ assets, mapsKey, currentStoreId }) => {
  const [stores, setStores] = useState([]);
  const [firstShownStores, setFirstShownStores] = useState([]);
  const [secondaryStores, setSecondaryStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [settingUserLocation, setSettingUserLocation] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [center, setCenter] = useState({
    lat: '',
    lng: '',
  });
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showMore, setShowMore] = useState(true);

  const { searchIcon, targetIcon, markerRed } = assets;

  const getUserLocationLoader = <div className="loader" />;

  const getStoreLocations = async search => {
    axios(`/change-branch/search-stores${search}`, {
      method: 'GET',
      dataType: 'json',
      headers: {
        'Content-Type': 'text/javascript',
        Accept: 'text/javascript',
      },
    })
      .then(({ data }) => {
        // cloning the object as the below calculations are changing the object structure
        const dataClone = JSON.parse(JSON.stringify(data));

        if (!dataClone.stores.length > 0) {
          return;
        }

        const half = Math.ceil(dataClone.stores.length / 2);
        let firstCollectionStores = [];
        let secondCollectionStores = [];

        if (dataClone.stores.length >= 5) {
          firstCollectionStores = dataClone.stores.splice(0, half);
          secondCollectionStores = dataClone.stores.splice(-half);
        } else {
          firstCollectionStores = dataClone.stores;
        }

        setStores(firstCollectionStores);
        setFirstShownStores(firstCollectionStores);
        setSecondaryStores(secondCollectionStores);
        setCenter(dataClone.center);
        setLoading(false);
        setShowMap(true);
        setShowError(false);
      })
      .catch(() => {
        setErrorMessage(
          `Sorry, no ${branchTermPlural} were found for your location. Please try again.`,
        );
        setShowError(true);
      });
  };

  // create map function after getting user location
  const createMap = ({ coords: { latitude, longitude } }) => {
    setSettingUserLocation(false);
    getStoreLocations(`?coordinates=${latitude},${longitude}`);
  };

  // Log error if unable to get location data using browser
  const logUserLocationError = () => {
    setErrorMessage(
      'An error occurred fetching your location. Please try again',
    );
    setShowError(true);
  };

  // Gets users location using browser navigator
  const getUserLocation = () => {
    setShowMap(false);
    if (navigator.geolocation) {
      setSettingUserLocation(true);
      navigator.geolocation.getCurrentPosition(createMap, logUserLocationError);
    } else {
      logUserLocationError();
    }
  };

  // show more results
  const showMoreStores = () => {
    setStores([...stores, ...secondaryStores]);
    setShowMore(!showMore);
  };

  // show less results
  const showLessStores = () => {
    setStores(firstShownStores);
    setShowMore(!showMore);
  };

  // Handle search from input
  const handleSearch = () => {
    if (searchTerm !== '') {
      getStoreLocations(`?postcode=${searchTerm}`);
    }
  };

  // Set store location against user
  const setLocationError = () => {
    setErrorMessage(
      `We were unable to set your ${branchTermSingular}. Please try again`,
    );
    setShowError(true);
  };

  const getInfoWindow = store => {
    const {
      name,
      email,
      telephone,
      line_1: line1,
      line_2: line2,
      line_3: line3,
      line_4: line4,
      line_5: line5,
      postcode,
      web_url: url,
    } = store;
    return `<div class="select-a-store-search__info-window-content">
            <p class="select-a-store-search__info-window-content__store-name">${name}</p>
            <p class="select-a-store-search__info-window-content__store-email"><span>Email:</span>${email}</p>
            <p class="select-a-store-search__info-window-content__store-telephone"><span>Telephone:</span>${telephone}</p>
            <ul class="select-a-store-search__info-window__address-list">
              <li>${line1}</li>
              <li>${line2}</li>
              <li>${line3}</li>
              <li>${line4}</li>
              <li>${line5}</li>
              <li>${postcode}</li>
            </ul>
            <a href="${url}" class="select-a-store-search__info-window__view-store">View ${branchTermSingularCapitalised} Profile</a>
            </div>`;
  };

  const selectAStoreMap = (
    <SelectAStoreMap
      defaultCenter={{
        lat: 54.7408371,
        lng: -4.1813227,
      }}
      mapsKey={mapsKey}
      id="collection-store-map"
      options={{
        center: {
          lat: center.lat,
          lng: center.lng,
        },
        zoom: 9,
      }}
      stores={stores}
      markerRed={markerRed}
      onMapLoad={map => {
        const infowindow = new google.maps.InfoWindow();
        let marker, i;

        for (i = 0; i < stores.length; i++) {
          marker = new google.maps.Marker({
            position: new google.maps.LatLng(
              stores[i].coordinates.latitude,
              stores[i].coordinates.longitude,
            ),
            map,
            icon: markerRed,
          });

          const contentString = getInfoWindow(stores[i]);

          google.maps.event.addListener(
            marker,
            'click',
            (function (marker, i) {
              return function () {
                infowindow.setContent(contentString);
                infowindow.open(map, marker);
              };
            })(marker, i),
          );
        }
      }}
    />
  );

  const selectAStoreStores = (
    <div className="select-a-store-search__store-results">
      {stores.length &&
        stores.map(store => (
          <SelectAStoreResult
            key={store.id}
            store={store}
            currentStoreId={currentStoreId}
            setLocationError={setLocationError}
          />
        ))}
      {stores.length && stores.length > 4 && (
        <div>
          <button
            type="button"
            className="select-a-store-search__store-results_more-results"
            onClick={() => {
              if (showMore) {
                showMoreStores();
              } else {
                showLessStores();
              }
            }}>
            {showMore ? 'Show More' : 'Show Less'}
          </button>
        </div>
      )}
    </div>
  );

  const locationError = (
    <div className="select-a-store-search__error">
      <p>{errorMessage}</p>
    </div>
  );

  return (
    <div>
      <h1>Select Collection {branchTermSingularCapitalised}</h1>
      <h2>Search for {branchTermPluralCapitalised}</h2>
      <div className="select-a-store-search">
        <input
          type="text"
          onChange={e => {
            e.persist();
            if (e.keyCode === 13) {
              if (e.target.value.length > 0) {
                getStoreLocations(`?postcode=${searchTerm}`);
              }
            } else {
              setSearchTerm(e.target.value);
            }
          }}
          spellCheck="false"
          className="select-a-store-search__input"
          aria-label="Select a store"
          placeholder="Enter your location or postcode"
        />
        <button
          type="submit"
          aria-label="search"
          className="select-a-store-search__submit"
          onClick={() => {
            handleSearch();
          }}>
          <img alt="" src={searchIcon} height={17} width={18} />
        </button>
      </div>
      <button
        type="button"
        onClick={() => {
          getUserLocation();
        }}
        className="select-a-store-search__user-location fl ai-c">
        <img alt="" src={targetIcon} height={17} width={18} />
        Use my current location
        {settingUserLocation && getUserLocationLoader}
      </button>
      {showError && locationError}
      {loading && (
        <div className="select-a-store-search__loading-locations">
          {getUserLocationLoader}
        </div>
      )}
      <div className="select-a-store-search__store-results-container">
        {showMap && selectAStoreStores}

        <div className="select-a-store-search__store-map">
          {showMap && selectAStoreMap}
        </div>
      </div>
    </div>
  );
};

export default SelectAStore;
